import { createContext, FC, PropsWithChildren, useContext } from "react";
import { useLocalStorage } from "rujira.ui";

const notificationApi = (): typeof Notification | undefined => {
  if (typeof window === "undefined") return undefined;
  return typeof window.Notification === "function"
    ? window.Notification
    : undefined;
};

const canRequestPermission = (): boolean => {
  try {
    return notificationApi()?.permission === "default";
  } catch {
    return false;
  }
};

const context = createContext<{
  dontShow: boolean;
  setDontShow: (v: boolean) => void;
  canRequestPermission: () => boolean;
  requestPermission: () => Promise<NotificationPermission>;
  push: (title: string, options?: NotificationOptions) => Notification | null;
}>({
  dontShow: false,
  setDontShow: () => {},
  canRequestPermission: () => false,
  requestPermission: async () => "denied",
  push: () => null,
});

export const NotificationContext: FC<PropsWithChildren> = ({ children }) => {
  const [dontShow, setDontShow] = useLocalStorage<boolean>(
    "rujira-push-dontshow",
    false
  );

  return (
    <context.Provider
      value={{
        dontShow,
        setDontShow,
        canRequestPermission,
        requestPermission: async () => {
          try {
            const api = notificationApi();
            if (!api) return "denied";
            return await api.requestPermission();
          } catch (cause) {
            console.warn("Notification permission is unavailable", cause);
            return "denied";
          }
        },
        push: (title: string, options?: NotificationOptions) => {
          try {
            const api = notificationApi();
            if (!api) return null;
            if (api.permission !== "granted") return null;
            return new api(title, options);
          } catch (cause) {
            // Some mobile browsers expose the API but reject constructors.
            console.warn("Browser notification is unavailable", cause);
            return null;
          }
        },
      }}>
      {children}
    </context.Provider>
  );
};

export const useNotifications = () => useContext(context);
